import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AddButton from 'components/v2/buttons/AddButton';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import {
  defaultLoad,
  dimensionalUnits,
  loadSchema,
  weightUnits,
  weightUnitsOptions,
} from 'utils/localization/types';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import { LocaleContext } from 'providers/locale-settings-context';
import Loads from './Loads';

const LoadDetailsForm = ({
  loadOnly,
  loads,
  fullTruckload,
  totalShipmentWeight,
  shipmentWeightUnit,
  addLoad,
  onRemoveLoad,
  onLoadFieldChange,
  onTotalShipmentWeightChange,
}) => {
  const { t } = useTranslation();
  const [selectedLoads, setSelectedLoads] = useState([]);
  const { localeData } = useContext(LocaleContext);
  useEffect(() => {
    setSelectedLoads(loads);
  }, [loads]);

  const addLoadhandler = () => {
    let prevLoads = [...selectedLoads];
    prevLoads.push({
      ...defaultLoad,
      weight_units:
        selectedLoads[0]?.weight_units ||
        localeData?.weight_units ||
        weightUnits[0],
      dimensional_units:
        selectedLoads[0]?.dimensional_units ||
        localeData?.dimensional_units ||
        dimensionalUnits[0],
    });
    addLoad(prevLoads);
  };

  const removeLoad = (index) => {
    return [
      ...selectedLoads.slice(0, index),
      ...selectedLoads.slice(index + 1),
    ];
  };

  const handleRemoveLoad = (index) => {
    const newLoads = removeLoad(index);
    onRemoveLoad(newLoads, index);
  };

  const handleLoadFieldChange = (field, value, index) => {
    const currentLoads = selectedLoads;
    let parsedValue;
    if (
      field === 'stackable' ||
      field === 'type' ||
      field === 'weight_units' ||
      field === 'dimensional_units'
    ) {
      parsedValue = value;
    } else if (field === 'weight' || field === 'height') {
      parsedValue = parseFloat(value);
      parsedValue = !Number.isNaN(parsedValue) ? parsedValue : undefined;
    } else {
      parsedValue = parseInt(value, 10);
      parsedValue = !Number.isNaN(parsedValue) ? parsedValue : undefined;
    }

    if (field === 'weight_units' || field === 'dimensional_units') {
      const loads = (currentLoads || []).map((load) => {
        return {
          ...load,
          [field]: parsedValue,
        };
      });
      return onLoadFieldChange(loads, field, value, index);
    }

    const load = {
      ...selectedLoads[index],
      [field]: parsedValue,
    };

    const loads = [
      ...currentLoads.slice(0, index),
      load,
      ...currentLoads.slice(index + 1),
    ];

    onLoadFieldChange(loads, field, value, index);
  };

  function renderShipmentWeight() {
    if (!fullTruckload) {
      return (
        <DescriptionListItem
          label={t('TOTAL_SHIPMENT_WEIGHT')}
          className="text-sm font-medium text-gray-500"
        >
          <dd className="mt-1 text-sm text-gray-900">
            {totalShipmentWeight} {shipmentWeightUnit}
          </dd>
        </DescriptionListItem>
      );
    }
    return (
      <div className="flex items-center mb-0 space-x-4">
        <CargomaticInput
          type="number"
          label={t('TOTAL_SHIPMENT_WEIGHT')}
          placeholder={t('TOTAL_SHIPMENT_WEIGHT')}
          min={0}
          value={totalShipmentWeight}
          onChange={(value) => onTotalShipmentWeightChange(value)}
        />
        <CargomaticSelect
          showCaption
          label={t('WEIGHT_UNIT')}
          selected={shipmentWeightUnit}
          className="w-20"
          items={weightUnitsOptions}
          onSelect={(item) => {
            handleLoadFieldChange('weight_units', item.value, 0);
          }}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="localizationSlug"
        />
      </div>
    );
  }

  return (
    <div className="my-6 space-y-4">
      <header className="flex items-center justify-between mb-0 space-x-4">
        {!fullTruckload && (
          <div className="flex items-center mb-0 space-x-4">
            <h3 className="my-0">{t('LOAD')}</h3>
            <AddButton onClick={addLoadhandler} />
          </div>
        )}
        {!loadOnly && renderShipmentWeight()}
      </header>
      {!fullTruckload && (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed default-table">
            <thead>
              <tr>
                <th className="" style={{ width: '150px' }}>
                  {t('TYPE')}
                </th>
                <th className="" style={{ width: '120px' }}>
                  {t('QUANTITY')}
                </th>
                <th className="" style={{ width: '120px' }}>
                  {t('LENGTH')}
                </th>
                <th className="" style={{ width: '120px' }}>
                  {t('WIDTH')}
                </th>
                <th className="" style={{ width: '120px' }}>
                  {t('HEIGHT')}
                </th>
                <th className="" style={{ width: '175px' }}>
                  {t('DIMENSIONAL_UNIT')}
                </th>
                {!loadOnly && (
                  <th className="" style={{ width: '120px' }}>
                    {t('TOTAL_WEIGHT')}
                  </th>
                )}
                {!loadOnly && (
                  <th className="" style={{ width: '175px' }}>
                    {t('WEIGHT_UNIT')}
                  </th>
                )}
                <th style={{ width: '120px' }}>{t('STACKABLE_TITLE')}?</th>
                <th style={{ width: '120px' }}>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {selectedLoads.map((load, i) => {
                return (
                  <Loads
                    loadOnly={loadOnly}
                    index={i}
                    key={i}
                    load={load}
                    onLoadFieldChange={(field, value) =>
                      handleLoadFieldChange(field, value, i)
                    }
                    onRemoveLoad={() => handleRemoveLoad(i)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

LoadDetailsForm.propTypes = {
  fullTruckload: PropTypes.bool.isRequired,
  loads: PropTypes.arrayOf(
    PropTypes.shape({
      ...loadSchema,
    }),
  ),
  onLoadFieldChange: PropTypes.func.isRequired,
  addLoad: PropTypes.func.isRequired,
  onRemoveLoad: PropTypes.func.isRequired,
  onTotalShipmentWeightChange: PropTypes.func.isRequired,
};

LoadDetailsForm.defaultProps = {
  loads: [],
};

export default LoadDetailsForm;
